/**
 * Copyright Mántum All Rights Reserved.
 */
import Component from './component';
import Validator from '../validator';
import useEmpty from '../../../hooks/useEmpty';
import { EVENT_CLEAR, ACTION_VALUE, useGroupDispatch } from '../../group';
import { forwardRef, useImperativeHandle, useRef } from 'react';

import type { Detail } from '@mantum/kanela';
import type { PropTypes } from './component';

export default forwardRef<Detail<unknown>, PropTypes>(
    function Input(props, ref) {
        const {
            error = false,
            name,
            omitValidation = false,
            optional = true
        } = props;

        const dispatch = useGroupDispatch();

        const empty = useEmpty(optional);
        const inputRef = useRef<HTMLInputElement>(null);

        useImperativeHandle(
            ref,
            () => {
                return {
                    name,
                    validable: !optional,
                    clear() {
                        empty.clear();
                        if (name) {
                            dispatch({
                                type: EVENT_CLEAR,
                                content: [
                                    {
                                        action: ACTION_VALUE,
                                        target: name
                                    }
                                ]
                            });
                        }
                    },
                    validate() {
                        return empty.validate(inputRef.current?.value);
                    },
                    value() {
                        return inputRef.current?.value;
                    }
                };
            },
            [name, optional, empty, dispatch]
        );

        const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            if (!omitValidation) {
                empty.validate(event.target.value);
            }

            if (props.onBlur) {
                props.onBlur(event);
            }
        };

        return (
            <Validator
                error={empty.value || error}
                message={props.message}
                mutate={props.mutate}
                type={props.type}
            >
                <Component
                    {...props}
                    error={empty.value || error}
                    onBlur={onBlur}
                    onChange={props.onChange}
                    ref={inputRef}
                />
            </Validator>
        );
    }
);
